import { Spinner } from '@gmg/gmg-react-components';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Role } from 'src/AppContext';
import { CPGO, ModuleId, PS_EDITIONS as PHOTOSHOP_EDITION_IDS, SETTINGS_MODAL_REGISTRY, SettingsModuleId } from 'src/graphql/mappers/module';
import { useGetUserDetails } from 'src/graphql/useGetUserDetails';
import { LicenseSystem } from 'src/viewModels/LicenseSystem';
import { ModuleLicense } from 'src/viewModels/Organization';
import AddOrEditUserModal, { AddOrEditUserModalForm, UserModalState } from './AddOrEditUserModal';
import { SingleModule } from './ModuleSelector';
import { AddOrEditUserSubmitData } from './UserManagement';

export interface AddOrEditUserModalContainerProps {
    userId?: string;
    onClose?: () => void;
    maintainableRoles: Array<Role>;
    availableGroups: Map<string, string>;
    availableModules: Map<ModuleId, ModuleLicense>;
    onHandleEditUser?: (
        userData: AddOrEditUserSubmitData,
        setUsermodalState: (props: UserModalState) => void,
        onClose: () => void,
    ) => void;
    onHandleInviteUser?: (
        userData: AddOrEditUserSubmitData,
        setUsermodalState: (props: UserModalState) => void,
        onClose: () => void,
    ) => void;
    availableLicenseSystems: Array<LicenseSystem>;
};

// We currently (2025-04-23) do not have contracts for ColorProof GO.
// So this checkbox should always be visible.
const ALWAYS_VISIBLE_MODULES: Array<ModuleId> = [CPGO.id];

const AddOrEditUserModalContainer: FC<AddOrEditUserModalContainerProps> = (props) => {
    const { t } = useTranslation();
    const useCase: 'new' | 'edit' = props.userId ? 'edit' : 'new';
    const [selectedModuleIds, setSelectedModuleIds] = useState<Array<ModuleId> | undefined>(useCase === 'new' ? [] : undefined);
    const [activeSettingsModal, setActiveSettingsModal] = useState<SettingsModuleId | undefined>(undefined);
    const [deselectedModuleIds, setDeselectedModuleIds] = useState<Set<ModuleId>>(new Set());
    const { data: userDetails, isFetching } = useGetUserDetails(props.userId);

    const hookFormMethods = useForm<AddOrEditUserModalForm>({
        mode: 'onBlur',
        defaultValues: {
            role: {
                value: 'Member',
                label: t('Roles.Member_label', 'Member'),
            },
        },
    });

    useEffect(() => {
        const isDataFetching = useCase === 'edit' && isFetching;
        if (useCase === 'new' || isDataFetching) return;

        setSelectedModuleIds(userDetails?.user.assignedModuleIds || []);

    }, [useCase, isFetching, userDetails?.user.assignedModuleIds]);

    if (selectedModuleIds === undefined) {
        return <Spinner label="Loading user data..." />;
    }

    const handleChangeModuleSelection = (clickedModuleId: ModuleId) => {
        setSelectedModuleIds((prev: ModuleId[] = []) => {
            const isPhotoShopOption = PHOTOSHOP_EDITION_IDS.includes(clickedModuleId);
            const isDeselection = prev!.includes(clickedModuleId);

            if (isPhotoShopOption && !isDeselection) {
                // Just one PhotoShop Edtion is allowed to be in the list!
                const modulesWithoutPsOptions = prev!.filter(id => !PHOTOSHOP_EDITION_IDS.includes(id));
                return [...modulesWithoutPsOptions, clickedModuleId];
            } else {
                // Toggle module selection
                if (isDeselection) {
                    setDeselectedModuleIds(prevIds => prevIds.add(clickedModuleId));
                    return prev!.filter(id => id !== clickedModuleId);
                } else {
                    setDeselectedModuleIds(prevIds => new Set([...prevIds].filter(id => id !== clickedModuleId)));
                    return [...prev!, clickedModuleId];
                }
            }
        });
    };
    const filteredAvailableModules: Array<SingleModule> = Array
        .from(props.availableModules)
        .filter(([id, module]) => ALWAYS_VISIBLE_MODULES.includes(id) || module.contractStatus.hasContract)
        .map(([id, currentModule]: [ModuleId, ModuleLicense]) => {
            const moduleInfo = currentModule.type === 'unlimited'|| currentModule.type === 'counterBased' // TODO: make it in the mapper function
                ? t('AddOrEditUserModal.product_info_unlimited_desc', 'Unlimited access')
                : t('AddOrEditUserModal.product_info_seat_availability_desc', {
                    defaultValue: '{{availableSeats}} out of {{limit}} seats available',
                    availableSeats: currentModule.effectiveNumberOfAvailableSeats,
                    limit: currentModule.totalNumberOfSeatsInContracts,
                });;
            const isModuleAssignedToUser = !!selectedModuleIds.includes(id);
            const selection: SingleModule = {
                id,
                name: currentModule.name,
                moduleInfo,
                isModuleAssignedToUser,
                isDisabled: !isModuleAssignedToUser && currentModule.effectiveNumberOfAvailableSeats < 1,
                hasAdditionalSettings: currentModule.hasAdditionalSettings,
            };
            return selection;
        });

    const ActiveModal = activeSettingsModal ? SETTINGS_MODAL_REGISTRY[activeSettingsModal] : null;

    return (
        <FormProvider {...hookFormMethods}>
            {ActiveModal
                ? <ActiveModal
                    onCloseModal={() => setActiveSettingsModal(undefined)}
                    userId={props.userId}
                    availableLicenseSystems={props.availableLicenseSystems}
                />
                : <AddOrEditUserModal
                    assignableRoles={useCase === 'edit'
                        ? Array.from(new Set([...props.maintainableRoles, userDetails!.user.role]))
                        : props.maintainableRoles}
                    availableGroups={props.availableGroups}
                    selectableModules={filteredAvailableModules}
                    initialValues={userDetails}
                    onHandleEditUser={props.onHandleEditUser}
                    onHandleInviteUser={props.onHandleInviteUser}
                    onClose={props.onClose!}
                    onShowSettingsModal={(moduleId: SettingsModuleId) => { setActiveSettingsModal(moduleId); }}
                    selectedModuleIds={selectedModuleIds}
                    onChangeModuleSelection={handleChangeModuleSelection}
                    deselectedModuleIds={deselectedModuleIds}
                />}
        </FormProvider>
    );
};

export default AddOrEditUserModalContainer;